import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, User, MessageSquare, Send, Flower2, Gem, Sparkles } from 'lucide-react';
import { submitLead } from '../lib/submissions';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });
      
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please contact us on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-amber-50/30 to-stone-50 relative overflow-hidden" data-testid="contact-section">
      
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Rings decoration */}
          <div className="flex justify-center mb-6">
            <Gem className="w-20 h-20 opacity-50 text-amber-500" />
          </div>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-400"></div>
            <Mail className="w-8 h-8 text-amber-600" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-emerald-900 mb-4" data-testid="contact-title">
            Get in Touch
          </h2>
          <p className="text-lg md:text-xl text-stone-600" data-testid="contact-subtitle">
            We'd love to hear from you
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          {/* Wedding card container */}
          <div className="relative bg-gradient-to-br from-white via-amber-50/20 to-white rounded-none shadow-2xl overflow-visible border border-amber-200">
            
            {/* Sketch decorations in corners */}
            <Flower2 className="absolute -top-8 -left-8 w-20 h-20 opacity-20 text-amber-600" />
            <Flower2 className="absolute -top-8 -right-8 w-20 h-20 opacity-20 text-amber-600 scale-x-[-1]" />
            <Flower2 className="absolute -bottom-8 -left-8 w-20 h-20 opacity-20 text-amber-600 scale-y-[-1]" />
            <Flower2 className="absolute -bottom-8 -right-8 w-20 h-20 opacity-20 text-amber-600 scale-[-1]" />
            
            {/* Lamp decoration at top */}
            <Sparkles className="absolute -top-12 left-1/2 -translate-x-1/2 w-12 h-12 opacity-25 text-amber-600" />
            
            <div className="p-10 md:p-16">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="contact-name" className="flex items-center gap-2 mb-2 text-emerald-900 font-semibold">
                      <User className="w-4 h-4 text-amber-600" />
                      Full Name *
                    </Label>
                    <Input
                      id="contact-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      data-testid="contact-name-input"
                      className="rounded-xl border-amber-200 focus:border-amber-400"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact-phone" className="flex items-center gap-2 mb-2 text-emerald-900 font-semibold">
                      <Phone className="w-4 h-4 text-amber-600" />
                      Phone Number *
                    </Label>
                    <Input
                      id="contact-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      data-testid="contact-phone-input"
                      className="rounded-xl border-amber-200 focus:border-amber-400"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="contact-email" className="flex items-center gap-2 mb-2 text-emerald-900 font-semibold">
                    <Mail className="w-4 h-4 text-amber-600" />
                    Email Address *
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    data-testid="contact-email-input"
                    className="rounded-xl border-amber-200 focus:border-amber-400"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contact-message" className="flex items-center gap-2 mb-2 text-emerald-900 font-semibold">
                    <MessageSquare className="w-4 h-4 text-amber-600" />
                    Message (Optional)
                  </Label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us how we can help you..."
                    rows={5}
                    data-testid="contact-message-input"
                    className="rounded-xl border-amber-200 focus:border-amber-400"
                  />
                </div>
                
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
              
              {/* Direct contact info */}
              <div className="mt-10 pt-10 border-t-2 border-amber-200 text-center">
                <p className="text-stone-600 mb-6 font-medium">Or reach us directly:</p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <a href="mailto:events@thaalikettu.com" className="flex items-center gap-2 text-emerald-800 hover:text-amber-700 transition-colors font-semibold">
                    <Mail className="w-5 h-5" />
                    events@thaalikettu.com
                  </a>
                  <span className="hidden sm:block text-stone-300 text-2xl">•</span>
                  <a href="tel:+919074500956" className="flex items-center gap-2 text-emerald-800 hover:text-amber-700 transition-colors font-semibold">
                    <Phone className="w-5 h-5" />
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
